import type { ClientNote } from '@/api/client-notes';
import { ImageThumbnails } from './ImageThumbnails';

interface NotesListProps {
  clientId: number;
  notes: ClientNote[];
  onSelect: (note: ClientNote) => void;
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMinutes < 1) return 'сейчас';
  if (diffMinutes < 60) return `${diffMinutes}м`;
  if (diffHours < 24) return `${diffHours}ч`;
  if (diffDays === 1) return 'вчера';
  if (diffDays < 7) return `${diffDays}д`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}нед`;
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

function truncateText(text: string, maxLines: number = 2): string {
  const lines = text.split('\n');
  if (lines.length <= maxLines) {
    // Also truncate very long single lines
    const joined = lines.join(' ');
    return joined.length > 100 ? joined.slice(0, 97) + '...' : text;
  }
  const truncated = lines.slice(0, maxLines).join(' ');
  return truncated.length > 100 ? truncated.slice(0, 97) + '...' : truncated + '...';
}

export function NotesList({ clientId, notes, onSelect }: NotesListProps) {
  if (notes.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-4">
        Заметок пока нет
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {notes.map((note) => (
        <button
          key={note.id}
          onClick={() => onSelect(note)}
          className="w-full text-left p-3 rounded-lg bg-muted/50 dark:bg-muted/30 hover:bg-muted transition-colors"
        >
          <div className="flex justify-between gap-2">
            <p className="text-sm text-foreground flex-1 min-w-0">
              {truncateText(note.text)}
            </p>
            <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
              {formatRelativeTime(note.createdAt)}
            </span>
          </div>
          {note.images.length > 0 && (
            <ImageThumbnails
              clientId={clientId}
              noteId={note.id}
              images={note.images}
              size="sm"
            />
          )}
        </button>
      ))}
    </div>
  );
}
